import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CircleHelpIcon,
  KeyboardIcon,
  LogOutIcon,
  PanelLeftIcon,
  PlusIcon,
  RefreshCwIcon,
  SearchIcon,
  SettingsIcon,
  SunMoonIcon,
} from "lucide-react"
import { useEffect, useState, type ReactNode } from "react"
import { Link, NavLink, useLocation, useMatch } from "react-router"
import type { Project } from "@beacon/core/projects"
import {
  ColourSchemeToggle,
  Disc,
  IdentityTile,
  Kbd,
  RoleGate,
  useViewport,
} from "@/design-system/index.ts"
import { installIcon, useInstallOffer } from "@/installed-app/install-offer.tsx"
import { reloadToUpdate, sayElsewhere, useUpdate } from "@/installed-app/updates.ts"
import { Button } from "@/primitives/button.tsx"
import { Drawer, DrawerContent, DrawerTitle } from "@/primitives/drawer.tsx"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from "@/primitives/sidebar.tsx"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/primitives/tooltip.tsx"
import { projectsByName } from "@/projects/order.ts"
import type { ShortcutId } from "@/shortcuts/registry.ts"
import { useShortcut } from "@/shortcuts/use-shortcut.ts"
import type { Me } from "@/sign-in/client.ts"
import { AccountMenu } from "./account-menu.tsx"
import { describeStanding } from "./describe.ts"
import {
  adminDestinations,
  designSystem,
  destinations,
  inAdministration,
  type Destination,
} from "./destinations.ts"
import { QueryDevtools } from "./query-devtools.tsx"
import { readFolds, writeFold, type SidebarSection } from "./sections.ts"

/*
 * The sidebar, on the canvas beside the page: the Member's own row that opens the account menu, the
 * command palette's search, the pages every Member has, their Projects with My Project first, and
 * at the foot the shortcuts and the collapse toggle. On an Administration page an Admin sees it
 * turned to Administration instead: the way back to where they were, and the Admin's pages. The
 * current page sits on a grey fill; nav is never teal. Collapsed to icons, each row names itself
 * and its G-chord in a tooltip.
 *
 * On the compact layout it is the avatar's drawer instead, a bottom sheet the tab bar opens: who is
 * signed in, "Update Beacon" while a new release waits, the pages the tabs don't hold, the
 * Projects, and the account menu's items laid out as rows (the colour scheme as a control in its
 * row, and "Install Beacon" where the browser can). The tab bar owns Search, so the drawer has no
 * search of its own, and it has nothing to collapse. It closes as any route is taken.
 */

/** How many of a Member's Projects the sidebar lists before "All Projects". */
const projectsShown = 8

/**
 * A page in the sidebar. A `nested` one stays current on the pages beneath it too, unless
 * `elsewhere` says another row is the current one there.
 */
function DestinationItem({
  destination: { to, title, Icon, chord, nested },
  badge,
  elsewhere = false,
}: {
  destination: Omit<Destination, "chord"> & { chord?: ShortcutId }
  badge?: number
  elsewhere?: boolean
}) {
  const current = useMatch({ path: to, end: !nested }) && !elsewhere
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        render={<NavLink to={to} end={!nested} />}
        isActive={Boolean(current)}
        tooltip={{
          children: (
            <>
              {title}
              {chord && <Kbd shortcut={chord} />}
            </>
          ),
        }}
      >
        <Icon />
        <span>{title}</span>
      </SidebarMenuButton>
      {badge !== undefined && badge > 0 && <SidebarMenuBadge>{badge}</SidebarMenuBadge>}
    </SidebarMenuItem>
  )
}

function ProjectItem({ project: { key, name } }: { project: Project }) {
  const current = useMatch(`/projects/${key}/*`)
  return (
    <SidebarMenuItem>
      <SidebarMenuButton render={<NavLink to={`/projects/${key}`} />} isActive={Boolean(current)}>
        <IdentityTile projectKey={key} size={16} />
        <span>{name}</span>
      </SidebarMenuButton>
    </SidebarMenuItem>
  )
}

/**
 * Whether a section is open, and the toggle that folds or opens it and remembers so in this browser.
 */
function useFold(section: SidebarSection) {
  const [open, setOpen] = useState(() => !readFolds()[section])
  function toggle() {
    setOpen(!open)
    writeFold(section, open)
  }
  return [open, toggle] as const
}

/**
 * A section's label as the button that folds it, with a chevron shown on hover, and to a keyboard
 * as it reaches the label, so the fold is seen both ways. Hidden on the icon rail, which has no
 * labels. On touch it is 44px tall, as a row is, since a hit area reaching past it would take the
 * top of the row below.
 */
function FoldLabel({
  open,
  onToggle,
  children,
}: {
  open: boolean
  onToggle: () => void
  children: string
}) {
  return (
    <SidebarGroupLabel
      render={<button type="button" onClick={onToggle} aria-expanded={open} />}
      className="gap-1 hover:text-sidebar-foreground group-data-[collapsible=icon]:hidden touch:h-11 [&>svg]:size-3.5"
    >
      {children}
      <ChevronRightIcon
        aria-hidden
        className={
          open
            ? "rotate-90 transition duration-100 reveal-on-hover-sidebar-group"
            : "transition duration-100 reveal-on-hover-sidebar-group"
        }
      />
    </SidebarGroupLabel>
  )
}

/**
 * My Project first, a row like the rest, then the Member's other Projects, up to eight by
 * name, then a link to the rest when there are more (the Projects page is a row above, so it isn't
 * repeated when every Project is listed). The list folds away under its label and offers a new
 * Project on hover. My Project stays when the list is folded and when the
 * sidebar is collapsed to icons, where the rest of the section is hidden.
 */
function ProjectsSection({
  projects,
  myProject,
}: {
  projects: readonly Project[]
  myProject: Destination | undefined
}) {
  const [open, toggle] = useFold("projects")
  const team = projects.filter((project) => !project.personal)
  const listed = projectsByName(team).slice(0, projectsShown)

  return (
    <SidebarGroup>
      <FoldLabel open={open} onToggle={toggle}>
        Projects
      </FoldLabel>
      <Tooltip>
        <TooltipTrigger
          render={
            <SidebarGroupAction
              render={<Link to="/projects?new=1" aria-label="New Project" />}
              className="reveal-on-hover-sidebar-group group-data-[collapsible=icon]:hidden"
            />
          }
        >
          <PlusIcon />
        </TooltipTrigger>
        <TooltipContent side="right">New Project</TooltipContent>
      </Tooltip>
      <SidebarGroupContent>
        <SidebarMenu>{myProject && <DestinationItem destination={myProject} />}</SidebarMenu>
      </SidebarGroupContent>
      {open && (
        <SidebarGroupContent className="group-data-[collapsible=icon]:hidden">
          <SidebarMenu>
            {listed.map((project) => (
              <ProjectItem key={project.key} project={project} />
            ))}
            {team.length > listed.length && (
              <SidebarMenuItem>
                <SidebarMenuButton
                  render={<Link to="/projects" />}
                  className="text-subtle-foreground"
                >
                  <span>All Projects</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            )}
          </SidebarMenu>
        </SidebarGroupContent>
      )}
    </SidebarGroup>
  )
}

/**
 * Administration, as the sidebar shows it on the Admin's pages: the row back to the page the Admin
 * came from, and the Admin's pages under their label, the design system's last.
 */
function AdministrationNav({ backTo }: { backTo: string }) {
  return (
    <>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              render={<Link to={backTo} />}
              tooltip="Back to Beacon"
              className="text-muted-foreground hover:text-foreground"
            >
              <ChevronLeftIcon />
              <span>Back to Beacon</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent role="navigation" aria-label="Administration">
        <SidebarGroup>
          <SidebarGroupLabel>Administration</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {[...adminDestinations, designSystem].map((destination) => (
                <DestinationItem key={destination.to} destination={destination} />
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </>
  )
}

/**
 * Where "Back to Beacon" goes: the last page the Member was on outside Administration, or Home when
 * they opened Beacon on an Administration page.
 */
function useBackTo(administration: boolean) {
  const { pathname, search } = useLocation()
  const here = `${pathname}${search}`
  const [backTo, setBackTo] = useState("/")
  if (!administration && backTo !== here) setBackTo(here)
  return backTo
}

/**
 * The foot: the shortcuts behind `?`, the cache inspector in development, and the toggle that
 * collapses the sidebar (⌘B too, which `AppSidebar` binds). The first two hide on the icon rail,
 * which has room for the toggle alone.
 */
function Foot({ onShowShortcuts }: { onShowShortcuts: () => void }) {
  const { state, toggleSidebar } = useSidebar()
  const collapsed = state === "collapsed"
  return (
    <SidebarFooter className="mt-auto flex-row items-center">
      <Tooltip>
        <TooltipTrigger
          render={
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Keyboard shortcuts"
              onClick={onShowShortcuts}
              className="group-data-[collapsible=icon]:hidden"
            />
          }
        >
          <CircleHelpIcon />
        </TooltipTrigger>
        <TooltipContent side="top">
          Keyboard shortcuts
          <Kbd shortcut="shortcuts" />
        </TooltipContent>
      </Tooltip>
      <span className="flex group-data-[collapsible=icon]:hidden">
        <QueryDevtools />
      </span>
      <Tooltip>
        <TooltipTrigger
          render={
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              onClick={toggleSidebar}
              className="ml-auto group-data-[collapsible=icon]:mr-auto"
            />
          }
        >
          <PanelLeftIcon />
        </TooltipTrigger>
        <TooltipContent side={collapsed ? "right" : "top"}>
          {collapsed ? "Expand sidebar" : "Collapse sidebar"}
          <Kbd shortcut="toggle-sidebar" />
        </TooltipContent>
      </Tooltip>
    </SidebarFooter>
  )
}

/**
 * The avatar's drawer on the compact layout: the sidebar's rows in a bottom sheet on the canvas,
 * opened from the tab bar through the sidebar's own mobile state, so ⌘B opens it too. Whatever
 * route is taken from it (or from anywhere), it closes.
 */
function AvatarDrawer({ me, children }: { me: Me; children: ReactNode }) {
  const { openMobile, setOpenMobile } = useSidebar()
  const { key } = useLocation()
  const update = useUpdate().state.status !== "current"
  useEffect(() => setOpenMobile(false), [key, setOpenMobile])
  // Open, the drawer leads with Update Beacon, and the notice would cover its last rows.
  useEffect(() => (openMobile && update ? sayElsewhere() : undefined), [openMobile, update])
  return (
    <Drawer open={openMobile} onOpenChange={setOpenMobile}>
      <DrawerContent className="bg-sidebar text-sidebar-foreground after:bg-sidebar">
        <DrawerTitle className="sr-only">Navigation</DrawerTitle>
        <div className="flex items-center gap-3 px-4 pt-1 pb-2">
          <Disc person={me} size={32} />
          <div className="min-w-0">
            <p className="truncate text-ui font-semibold">{me.name}</p>
            <p className="truncate text-xs text-sidebar-muted-foreground">{describeStanding(me)}</p>
          </div>
        </div>
        {/* The release the avatar's dot points at, first, where the dot leads the eye. */}
        {update && (
          <SidebarGroup className="pt-0">
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton onClick={reloadToUpdate} className="text-sidebar-foreground">
                  <RefreshCwIcon />
                  <span>Update Beacon</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroup>
        )}
        <div className="flex flex-col pb-2">{children}</div>
      </DrawerContent>
    </Drawer>
  )
}

/**
 * The account menu's items as the drawer's last rows: the pages a Member sets up once,
 * Administration for an Admin who isn't in it already, the colour scheme, installing Beacon where
 * the browser can, the shortcuts on a keyboard, and signing out.
 */
function AccountRows({
  me,
  administration,
  onShowShortcuts,
  onSignOut,
}: {
  me: Me
  administration: boolean
  onShowShortcuts: () => void
  onSignOut: () => void
}) {
  const { setOpenMobile } = useSidebar()
  const { offered, install } = useInstallOffer()
  const InstallIcon = installIcon(true)
  return (
    <SidebarGroup>
      <SidebarGroupLabel>Account</SidebarGroupLabel>
      <SidebarGroupContent>
        <SidebarMenu>
          {destinations
            .filter((destination) => destination.accountMenu)
            .map((destination) => (
              <DestinationItem key={destination.to} destination={destination} />
            ))}
          {/* Administration opens on its first page, turning the drawer to the Admin's pages. */}
          {!administration && (
            <RoleGate role={me.role} admits="admin">
              <SidebarMenuItem>
                <SidebarMenuButton render={<Link to={adminDestinations[0]?.to ?? "/"} />}>
                  <SettingsIcon />
                  <span>Administration</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </RoleGate>
          )}
          <SidebarMenuItem className="flex h-7 items-center gap-2 px-2 text-ui font-medium text-sidebar-muted-foreground touch:h-11 [&>svg]:size-4 [&>svg]:shrink-0">
            <SunMoonIcon aria-hidden />
            <span>Theme</span>
            <span className="ml-auto">
              <ColourSchemeToggle />
            </span>
          </SidebarMenuItem>
          {offered && (
            <SidebarMenuItem>
              {/* The drawer makes way for the browser's prompt, or the sheet with the steps. */}
              <SidebarMenuButton
                onClick={() => {
                  setOpenMobile(false)
                  install()
                }}
              >
                <InstallIcon />
                <span>Install Beacon</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}
          <SidebarMenuItem className="touch:hidden">
            <SidebarMenuButton onClick={onShowShortcuts}>
              <KeyboardIcon />
              <span>Keyboard shortcuts</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton onClick={onSignOut}>
              <LogOutIcon />
              <span>Sign out</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  )
}

export function AppSidebar({
  me,
  projects,
  openTickets,
  onSearch,
  onShowShortcuts,
  onSignOut,
}: {
  me: Me
  /** The Member's Projects that aren't archived. */
  projects: readonly Project[]
  /** How many open tickets are assigned to the Member, once known. */
  openTickets: number | undefined
  onSearch: () => void
  onShowShortcuts: () => void
  onSignOut: () => void
}) {
  const [home, myTickets, ...others] = destinations
  // My Project leads the Projects section rather than sitting among these, and the account menu
  // lists its own.
  const shown = others.filter(
    (destination) => destination.to !== "/my-project" && !destination.accountMenu,
  )
  // Once their Personal Project is made, My Project goes straight to its Board, and stays current
  // on its pages; until then it goes to the page that makes it.
  const personal = projects.find((project) => project.personal)
  const myProjectPage = destinations.find((destination) => destination.to === "/my-project")
  const myProject =
    myProjectPage && personal
      ? { ...myProjectPage, to: `/projects/${personal.key}`, nested: true as const }
      : myProjectPage
  // On its pages My Project is the current row, not Projects, which holds every other Board.
  const onPersonal = Boolean(useMatch(`/projects/${personal?.key ?? "-"}/*`)) && Boolean(personal)
  // Administration's pages turn the sidebar to it for an Admin. A Member who isn't one keeps the
  // app's sidebar on the one such page that answers them too (Jira).
  const { pathname } = useLocation()
  const administration = me.role === "admin" && inAdministration(pathname)
  const backTo = useBackTo(administration)
  const { compact } = useViewport()
  // ⌘B collapses the sidebar, or on the compact layout opens and closes the avatar's drawer. It is
  // bound through the registry like every other shortcut, so it leaves ⌘B to a field (bold, in the
  // markdown editor) and to a dialog open over the page.
  const { toggleSidebar } = useSidebar()
  useShortcut("toggle-sidebar", toggleSidebar)

  if (compact) {
    const account = (
      <AccountRows
        me={me}
        administration={administration}
        onShowShortcuts={onShowShortcuts}
        onSignOut={onSignOut}
      />
    )
    return (
      <AvatarDrawer me={me}>
        {administration ? (
          <AdministrationNav backTo={backTo} />
        ) : (
          <SidebarContent role="navigation" aria-label="Main" className="overflow-visible">
            {/* Home, My tickets and Projects are tabs; the drawer holds the rest. */}
            <SidebarGroup>
              <SidebarGroupContent>
                <SidebarMenu>
                  {shown
                    .filter((destination) => !destination.tab)
                    .map((destination) => (
                      <DestinationItem key={destination.to} destination={destination} />
                    ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
            <ProjectsSection projects={projects} myProject={myProject} />
          </SidebarContent>
        )}
        {account}
      </AvatarDrawer>
    )
  }

  if (administration) {
    return (
      <Sidebar variant="inset" collapsible="icon">
        <AdministrationNav backTo={backTo} />
        <Foot onShowShortcuts={onShowShortcuts} />
        <SidebarRail />
      </Sidebar>
    )
  }

  return (
    <Sidebar variant="inset" collapsible="icon">
      <SidebarHeader>
        <div className="flex h-9 items-center gap-1">
          <AccountMenu me={me} onShowShortcuts={onShowShortcuts} onSignOut={onSignOut} />
          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Search"
                  onClick={onSearch}
                  className="group-data-[collapsible=icon]:hidden"
                />
              }
            >
              <SearchIcon />
            </TooltipTrigger>
            <TooltipContent side="bottom">
              Search
              <Kbd shortcut="command-palette" />
            </TooltipContent>
          </Tooltip>
        </div>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={onSearch}
              tooltip={{
                children: (
                  <>
                    Search or jump to…
                    <Kbd shortcut="command-palette" />
                  </>
                ),
              }}
              // A quiet field on the canvas, which opens the palette rather than taking text. Its
              // gaps and its end are a step tighter than a nav row's, so the whole label fits
              // beside Ctrl K, which is wider than ⌘K; the keys never give way, the label would
              // truncate first.
              className="gap-1.5 pr-1.5 bg-background/60 font-normal text-muted-foreground ring-1 ring-sidebar-border hover:bg-background hover:text-foreground"
            >
              <SearchIcon />
              <span className="truncate">Search or jump to…</span>
              <span className="ml-auto shrink-0 group-data-[collapsible=icon]:hidden">
                <Kbd shortcut="command-palette" />
              </span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      {/* The pages, as the one navigation landmark, so a screen reader can reach or skip them. */}
      <SidebarContent role="navigation" aria-label="Main">
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {home && <DestinationItem destination={home} />}
              {myTickets && <DestinationItem destination={myTickets} badge={openTickets} />}
              {shown.map((destination) => (
                <DestinationItem
                  key={destination.to}
                  destination={destination}
                  elsewhere={destination.to === "/projects" && onPersonal}
                />
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        <ProjectsSection projects={projects} myProject={myProject} />
      </SidebarContent>
      <Foot onShowShortcuts={onShowShortcuts} />
      <SidebarRail />
    </Sidebar>
  )
}
